import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import phone from "phone";
import { Title , ReadOnly } from "../../common";
import Loading from "../../common/Loading"; // TODO(viktor): needed for "new-stlye" loading
import PhoneNumberInput from "../../common/PhoneNumberInput";
import VerificationCodeInput from "../../common/VerificationCodeInput";
import {
  UPDATE_VERIFY_PHONE_STEP,
  VERIFICATION_CODE_SEND,
  VERIFICATION_CODE_CHECK,
} from "./mutations"; // TODO(viktor): decide whether to keep these here to lump all in other folder
import { updateToNextStep } from "../../../state/actions";
import { useCurrentStep } from "../../../state/store/customHooks";
import { GQL_FETCH_POLICY_CACHE_AND_NETWORK_ONLY, IS_MOBILE, ONBOARDING_STEPS } from "../../../constants"; // TODO(viktor): Do we need URI_PATHS to manually scoot to next step?
import CheckmarkIcon from "../../../assets/CheckmarkIcon";
import withQuery from "../wrappers/gql/withQuery";
import { GET_APPLICATION_INFO } from "../../../api/gql";
import { sanitizePhoneNumber } from "../../../utilities/userInfo";

// TODO(viktor): move somewhere
const RESPONSE_SUCCESS_TYPE = "SUCCESS";
const RESPONSE_ERROR_TYPE = "ERROR";
const RESPONSE_MESSAGE_INCORRECT_CODE = "Verification code is incorrect";
const RESPONSE_MESSAGE_MAX_ATTEMPTS_REACHED = "Error: Max send attempts reached";
const TITLES = {
  CONFIRMATION_CODE: "Enter confirmation code",
  VERIFY_PHONE_NUMBER: "Verify your phone number",
};
const TEXT = {
  FRAUD_PROMPT: "Did you know? Verifying your phone number helps stop fraud",
  PHONE_NUMBER_VERIFIED: "Your phone number has been verified.",
  ENTER_CODE_PROMPT: "Please enter the 5 digit code sent to your number",
  ERROR_TEXT_SMS_MAX_ATTEMPTS: "Please wait a few minutes before trying again",
  ERROR_TEXT_CODE_VERIFICATION_ERROR: "Error verifying code, please try again later",
  ERROR_TEXT_INVALID_CODE_ENTRY: "Verification code is incorrect",
  ERROR_TEXT_INVALID_PHONE_ENTRY: "Valid US phone number required",
};

const VerifyPhoneV2 = (props) => {
  const dispatch = useDispatch();
  const { history, data: applicationInfo } = props;
  const { phoneNumber: phoneNumberFromDb } = applicationInfo.getApplicationInfo;

  // State
  const [phoneNumber, setPhoneNumber] = useState(sanitizePhoneNumber(phoneNumberFromDb));
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);
  const [error, setError] = useState(null); // TODO(viktor): Consider separate error states

  // Mutations
  const [verificationCodeSend, { loading: sendCodeLoading }] = useMutation(
    VERIFICATION_CODE_SEND,
  );
  const [verificationCodeCheck, { loading: checkCodeLoading }] = useMutation(
    VERIFICATION_CODE_CHECK,
  );
  const [updatePhoneStep] = useMutation(UPDATE_VERIFY_PHONE_STEP);

  const loading = sendCodeLoading || checkCodeLoading;
  const currentStep = useCurrentStep();
  const readOnlyMode = currentStep !== ONBOARDING_STEPS.Phone;
  // TODO(viktor): do we want to preserve window.onbeforeunload?

  useEffect(() => {
    if (verificationCode.length === 5)
      checkSMSCode();

  }, [verificationCode]);

  useEffect(() => {
    setError(null);
  }, [phoneNumber, verificationCode]);

  // TODO: scoot this into one of the other functions
  const preparePhoneString = (str) => {
    const newStr = str.replace(/[^A-Z0-9]/gi, "");
    return `+1${newStr}`;
  };

  const validatePhoneInput = () => {
    if (!phoneNumber || !phoneNumber.length) {
      setError("Please enter a phone number");
      return false;
    }

    const preparedString = preparePhoneString(phoneNumber);
    const validatedPhone = phone(preparedString, {
      country: "USA",
    });
    if (validatedPhone.isValid) return true;

    setError("Please enter a valid phone number");
    return false;
  };

  const validateCodeInput = () => {
    if (!verificationCode || verificationCode.length !== 5) {
      setError("Please enter a verification code");
      return false;
    }
    return true;
  };

  const sendCode = async () => {
    // TODO(viktor): push to next if step is complete?
    if (loading) return;

    if (!validatePhoneInput()) return;

    setError(null);
    const { data } = await verificationCodeSend({
      variables: { phoneNumber: preparePhoneString(phoneNumber) },
    });

    const { status, message } = data.verificationCodeSend;

    if (status === RESPONSE_SUCCESS_TYPE) {
      setError(null);
      setVerificationResent(false);

      // TODO(viktor): why was this logic written this way?
      if (!verificationSent)
        setVerificationSent(true);

    }

    if (message === RESPONSE_MESSAGE_MAX_ATTEMPTS_REACHED) {
      setError(TEXT.ERROR_TEXT_SMS_MAX_ATTEMPTS);
      setVerificationResent(false);
      return;
    }

    if (status === RESPONSE_ERROR_TYPE)
      setError("There was an error with our servers. Please try again");
  };

  const resendCode = async () => {
    if (loading) return;
    await sendCode();
    setVerificationResent(true);
  };

  const nextStep = () => {
    dispatch(updateToNextStep(history));
  };

  const checkSMSCode = async () => {
    if (loading) return;

    if (!validateCodeInput()) return;

    try {
      const response = await verificationCodeCheck({
        variables: {
          phoneNumber: preparePhoneString(phoneNumber),
          code: verificationCode,
        },
      });

      const { status, message } = response.data.verificationCodeCheck;

      if (message === RESPONSE_MESSAGE_INCORRECT_CODE) {
        setError(TEXT.ERROR_TEXT_INVALID_CODE_ENTRY);
        return;
      }

      if (status === RESPONSE_ERROR_TYPE) {
        setError(TEXT.ERROR_TEXT_CODE_VERIFICATION_ERROR);
        return;
      }

      await updatePhoneStep();
      nextStep();
    } catch (e) {
      // TODO(viktor): better error-handling
      console.error(`An error occurred:`, e.message);
    }
  };

  const handleNext = () => {
    if (readOnlyMode) {
      nextStep();
      return;
    }

    if (verificationSent) {
      checkSMSCode();
      return;
    }

    sendCode();
  };

  // TODO(viktor): fix entry of > 10 characters; this also occurs on prod
  const handlePhoneInputChanged = (_, ayt) => {
    setError(null);
    const number = ayt?.nationalNumber;

    if (!number) {
      setPhoneNumber(null);
      return;
    }

    if (number.length <= 10)
      setPhoneNumber(number);

    if (number.length === 10) {
      const validatedPhone = phone(number, "USA");
      if (validatedPhone.isValid)
        setPhoneNumber(sanitizePhoneNumber(validatedPhone.phoneNumber));
        // TODO(viktor): make sure we don't need to set country code here
      else
        setError(TEXT.ERROR_TEXT_INVALID_PHONE_ENTRY);

    }
  };

  // TODO(viktor): this way, or replicate dev loading (spinner on button)?
  // if (sendCodeLoading || checkCodeLoading) {
  //   return <Loading className="self-center justify-center h-full" />;
  // }

  return (
    <div className="outerStack-mobile sm:outerStack">
      <div
        className={
          verificationSent ? "innerStack-mobile md:innerStack" : "innerStack"
        }
      >
        <Title
          text={
            verificationSent
              ? TITLES.CONFIRMATION_CODE
              : TITLES.VERIFY_PHONE_NUMBER
          }
          disabled={readOnlyMode}
          disabledText={TEXT.PHONE_NUMBER_VERIFIED}
        />
        {verificationSent ? (
          <div>
            <p className="w-10/12 mx-auto font-open-sans">
              {TEXT.ENTER_CODE_PROMPT}{" "}
              <span className="text-primary">
                  +x xxx xxx-
                {phoneNumber.slice(-4)}
              </span>{" "}
              <span
                className="text-gray-primary hover:text-black cursor-pointer"
                onClick={() => {
                  setPhoneNumber("");
                  setVerificationSent(false);
                }}
              >
                  (<span>edit</span>)
              </span>
            </p>
            <VerificationCodeInput
              onChange={(newValue) => {
                setVerificationCode(newValue);
              }}
              autofocus
              disabled={loading}
              style={{ marginTop: 25 }}
              fontSize={4}
            />
          </div>
        ) : (
          <ReadOnly readOnly={readOnlyMode}>
            <div className="mt-6">
              <PhoneNumberInput
                fontSize={IS_MOBILE ? 2.2 : 3}
                onChange={handlePhoneInputChanged}
                onEnter={sendCode}
                value={phoneNumber}
                autofocus
                disabled={loading || readOnlyMode}
              />
            </div>
          </ReadOnly>
        )}
        <div>
          {error && (
            <div className="text-red-400 font-open-sans text-sm px-3 mt-3">
              {" "}
              <i className="far fa-solid fa-triangle-exclamation mr-1" />
              {error}
            </div>
          )}

          <button
            className="continueButton cursor-pointer"
            disabled={loading}
            style={{
              backgroundColor: loading ? "gray" : "#26E3BA",
            }}
            onClick={handleNext}
          >
            {" "}
            {readOnlyMode ? (
              "Continue"
            ) : loading ? (
              <i className="fas fa-solid fa-circle-notch fa-spin m-auto text-2xl" />
            ) : verificationSent ? (
              "Submit"
            ) : (
              "Send Code"
            )}{" "}
          </button>
          {verificationSent && (
            <>
              {verificationResent ? (
                <div>
                  <CheckmarkIcon
                    style={{ display: "inline", marginRight: 10 }}
                  />
                  <span>SMS resent</span>
                </div>
              ) : (
                <div className="textContainer">
                  <div className="w-9/12 sm:w-10/12 mx-auto">
                    <span
                      className="cursor-pointer hover:underline"
                      style={{ color: "#0DE0AA" }}
                      onClick={resendCode}
                    >
                      {" "}
                        Re-send SMS
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {!verificationSent && (
          <div className="textContainer">{TEXT.FRAUD_PROMPT}</div>
        )}
      </div>
    </div>
  );
};

export default withQuery(GET_APPLICATION_INFO, GQL_FETCH_POLICY_CACHE_AND_NETWORK_ONLY)(VerifyPhoneV2);
